import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, Check, LifeBuoy, Loader2, MessageCircle } from 'lucide-react';
import type { ConfirmCardPaymentResult } from '@flyingmuse/shared';
import { confirmCardPayment } from '../lib/booking';
import {
  forgetCardCheckout,
  markPurchaseReported,
  readCardCheckout,
  wasPurchaseReported,
  type CardCheckoutHandoff,
} from '../lib/cardCheckout';
import {
  trackCardCheckoutReturn,
  trackCardPaymentFailed,
  trackCardPaymentUnresolved,
  trackCardPurchase,
  trackWhatsAppClick,
} from '../lib/analytics';
import { whatsappLink } from '../data/site';

/**
 * Where Viva Smart Checkout sends the guest after a successful (or pending)
 * payment. Viva appends `s` (order code) and `t` (transaction id).
 *
 * The query string is only a hint — `confirmCardPayment` re-verifies the
 * payment with Viva server-side. The webhook settles the same payment
 * independently, so a guest who closes this tab still gets their booking; that
 * is also why an unresolved status here is worth waiting on rather than
 * treating as a failure.
 */
const POLL_INTERVAL_MS = 2_500;
const MAX_ATTEMPTS = 10;

type View =
  | { kind: 'working' }
  | { kind: 'done'; result: ConfirmCardPaymentResult }
  | { kind: 'timeout' }
  | { kind: 'missing' };

export function BookingReturnPage() {
  const [params] = useSearchParams();
  const orderCode = params.get('s');
  const transactionId = params.get('t');

  const [view, setView] = useState<View>(
    orderCode ? { kind: 'working' } : { kind: 'missing' },
  );
  const [handoff] = useState<CardCheckoutHandoff | null>(() =>
    readCardCheckout(orderCode),
  );
  const trackedRef = useRef(false);

  useEffect(() => {
    if (!orderCode) return;
    let cancelled = false;
    let attempts = 0;

    async function poll() {
      attempts += 1;
      try {
        const result = await confirmCardPayment({
          orderCode: orderCode as string,
          ...(transactionId ? { transactionId } : {}),
        });
        if (cancelled) return;

        if (result.status !== 'pending') {
          setView({ kind: 'done', result });
          return;
        }
      } catch {
        // Transient — the webhook is still working on it. Fall through to retry.
        if (cancelled) return;
      }

      if (attempts >= MAX_ATTEMPTS) {
        setView({ kind: 'timeout' });
        return;
      }
      window.setTimeout(poll, POLL_INTERVAL_MS);
    }

    void poll();
    return () => {
      cancelled = true;
    };
  }, [orderCode, transactionId]);

  // Report the outcome to GA4 exactly once per visit — and, for a purchase,
  // exactly once per order across visits.
  useEffect(() => {
    if (!orderCode) return;
    if (view.kind !== 'done' && view.kind !== 'timeout') return;
    if (trackedRef.current) return;
    trackedRef.current = true;

    const status = view.kind === 'timeout' ? 'unresolved' : view.result.status;
    trackCardCheckoutReturn(status, orderCode);

    if (view.kind === 'timeout') {
      trackCardPaymentUnresolved(orderCode, 'timeout', handoff?.amount);
      return;
    }

    if (view.result.status === 'failed') {
      trackCardPaymentFailed(orderCode, 'Viva reported the payment as failed');
      forgetCardCheckout();
      return;
    }

    if (view.result.status === 'needsAttention') {
      trackCardPaymentUnresolved(
        orderCode,
        'needsAttention',
        view.result.amount ?? handoff?.amount,
      );
      return;
    }

    // Paid. A reload or a revisit from history remounts this page, so the
    // purchase is de-duplicated by order code — GA4 does not reliably drop
    // repeats of the same transaction_id, and this one carries revenue.
    if (wasPurchaseReported(orderCode)) return;
    markPurchaseReported(orderCode);
    trackCardPurchase({
      bookingCode: view.result.bookingCode ?? orderCode,
      value: view.result.amount ?? handoff?.amount ?? 0,
      guests: handoff?.guests ?? 0,
      experienceAlias: handoff?.experienceAlias ?? null,
      experienceTitle: handoff?.experienceTitle ?? null,
      optionTitle: handoff?.optionTitle ?? null,
      bookingDate: handoff?.activityDateTime ?? '',
    });
    forgetCardCheckout();
  }, [view, handoff, orderCode]);

  return (
    <main className="mx-auto flex min-h-[70vh] w-full max-w-xl flex-col justify-center px-6 py-20">
      {view.kind === 'working' && (
        <Panel
          icon={<Loader2 className="h-7 w-7 animate-spin text-crimson" />}
          title="Confirming your payment…"
          body="This only takes a moment. Please don't close this page."
        />
      )}

      {view.kind === 'missing' && (
        <Panel
          tone="warn"
          icon={<AlertCircle className="h-7 w-7 text-crimson" />}
          title="Nothing to confirm"
          body="We couldn't find a payment reference in this link. If you've just paid, check your email for the confirmation — or message us and we'll look it up."
          action={<HomeLink />}
        />
      )}

      {view.kind === 'timeout' && (
        <Panel
          icon={<Loader2 className="h-7 w-7 animate-spin text-crimson" />}
          title="Still confirming"
          body="Your payment is going through — it's taking a little longer than usual. You'll get a confirmation email as soon as it lands, and nothing more is needed from you."
          action={<WhatsAppLink />}
        />
      )}

      {view.kind === 'done' && view.result.status === 'paid' && (
        <Panel
          icon={<Check className="h-7 w-7 text-crimson" strokeWidth={2.2} />}
          title="You're booked!"
          body={
            handoff?.email
              ? `Payment received and your reservation is confirmed. A confirmation email is on its way to ${handoff.email}.`
              : 'Payment received and your reservation is confirmed. A confirmation email is on its way.'
          }
        >
          {view.result.bookingCode && (
            <div className="mx-auto mt-5 inline-block rounded-lg border border-charcoal/15 bg-cream-soft px-5 py-3">
              <p className="font-sans text-[0.62rem] font-semibold uppercase tracking-label text-charcoal/55">
                Booking reference
              </p>
              <p className="mt-1 font-display text-xl font-medium tracking-wide text-charcoal">
                {view.result.bookingCode}
              </p>
            </div>
          )}
          <div className="mt-7">
            <HomeLink />
          </div>
        </Panel>
      )}

      {view.kind === 'done' && view.result.status === 'needsAttention' && (
        <Panel
          icon={<LifeBuoy className="h-7 w-7 text-crimson" strokeWidth={1.8} />}
          title="Payment received"
          body="Your card payment went through, but we need to finish your reservation by hand. Nothing further to pay — we'll email you shortly to confirm the details. Message us if you'd like it sorted right away."
          action={<WhatsAppLink />}
        />
      )}

      {view.kind === 'done' && view.result.status === 'failed' && (
        <Panel
          tone="warn"
          icon={<AlertCircle className="h-7 w-7 text-crimson" />}
          title="Payment not completed"
          body="Your card was not charged and the slot has been released. You can try booking again, or message us and we'll sort it out with you."
          action={<HomeLink label="Back to experiences" to="/experiences" />}
        />
      )}
    </main>
  );
}

function Panel({
  icon,
  title,
  body,
  action,
  children,
  tone = 'default',
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  action?: React.ReactNode;
  children?: React.ReactNode;
  tone?: 'default' | 'warn';
}) {
  return (
    <div className="text-center">
      <div
        className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ${
          tone === 'warn' ? 'bg-crimson/10' : 'bg-crimson/10'
        }`}
      >
        {icon}
      </div>
      <h1 className="mt-5 font-display text-3xl font-medium text-charcoal">
        {title}
      </h1>
      <p className="mx-auto mt-3 max-w-md font-sans text-sm font-light leading-relaxed text-stone">
        {body}
      </p>
      {children}
      {action && <div className="mt-7">{action}</div>}
    </div>
  );
}

function HomeLink({ label = 'Done', to = '/' }: { label?: string; to?: string }) {
  return (
    <Link to={to} className="btn btn-primary">
      {label}
    </Link>
  );
}

function WhatsAppLink() {
  return (
    <a
      href={whatsappLink(
        "Hi Flying Muse! I've just paid for a booking on flyingmuse.gr and would like to check it's confirmed.",
      )}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackWhatsAppClick('payment_unresolved')}
      className="btn btn-primary inline-flex items-center gap-2"
    >
      <MessageCircle className="h-4 w-4" />
      Message us on WhatsApp
    </a>
  );
}
